import React, { useState, useEffect } from 'react';
import { Card, CardBody, Badge, Collapse } from 'reactstrap';
import { ApiRequest } from '../../../../../../../../GlobalComponents/Builders';
import { CheckSquare, Square, AlertTriangle, ChevronDown, ChevronRight } from 'react-feather';

const STATUS_OPTIONS = [
    { value: 'in_scope', label: 'In Scope', color: 'success' },
    { value: 'out_of_scope', label: 'Out of Scope', color: 'secondary' },
    { value: 'tbd', label: 'TBD', color: 'warning' },
];

const CATEGORY_LABELS = {
    core: { label: 'Core', color: 'primary' },
    addon: { label: 'Add-on', color: 'info' },
    setup: { label: 'Setup', color: 'dark' },
};

const MigrationScopeSection = ({
    sections,
    setSections,
    services = [],
    savedVersion = 0,
    isEditing = false,
    onDirtyChange,
}) => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expandedSections, setExpandedSections] = useState({ core: true, addon: true, setup: false, tbd: true });
    const [initialCategories, setInitialCategories] = useState({});

    const toggleSection = (section) => setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));

    const overview = sections.overview || {};
    const scopeData = sections.migration_scope || { items: [] };
    const items = scopeData.items || [];

    // Resolve source service ID from service name
    const sourceService = services.find(s => s.name === overview.source_system);
    const destService = services.find(s => s.name === overview.destination_system);
    const sourceServiceId = sourceService?.service_id;
    const destServiceId = destService?.service_id;

    // Load templates when source/dest services are known
    useEffect(() => {
        if (!sourceServiceId) {
            setTemplates([]);
            return;
        }
        setLoading(true);
        const params = { sourceServiceId: String(sourceServiceId) };
        if (destServiceId) params.destinationServiceId = String(destServiceId);

        ApiRequest('GET', '/dmt/scopeTemplates', { queryParams: params }).then(res => {
            setLoading(false);
            if (res.success && res.data?.templates) {
                const loaded = res.data.templates || [];
                setTemplates(loaded);
                // Snapshot effective categories on load (items override template default)
                const catSnapshot = {};
                loaded.forEach(t => {
                    const itemState = items.find(i => i.code === t.code);
                    catSnapshot[t.code] = itemState?.category || t.category;
                });
                setInitialCategories(catSnapshot);
            }
        });
    }, [sourceServiceId, destServiceId]);

    // Re-snapshot categories after a successful save
    useEffect(() => {
        if (savedVersion > 0 && templates.length > 0) {
            const catSnapshot = {};
            templates.forEach(t => {
                const itemState = items.find(i => i.code === t.code);
                catSnapshot[t.code] = itemState?.category || t.category;
            });
            setInitialCategories(catSnapshot);
        }
    }, [savedVersion]);

    // Get the current item state for a template
    const getItemState = (code) => {
        return items.find(i => i.code === code) || null;
    };

    // Update an item's status or notes
    const updateItem = (template, field, value) => {
        const existing = [...items];
        const idx = existing.findIndex(i => i.code === template.code);

        if (idx >= 0) {
            existing[idx] = { ...existing[idx], [field]: value };
        } else {
            existing.push({
                code: template.code,
                name: template.name,
                status: 'tbd',
                category: template.category,
                notes: '',
                [field]: value,
            });
        }

        setSections(prev => ({ ...prev, migration_scope: { ...scopeData, items: existing } }));
        if (onDirtyChange) onDirtyChange(true);
    };

    // Check if a dependency is missing (not in_scope)
    const getMissingDependencies = (template) => {
        if (!template.dependencies || template.dependencies.length === 0) return [];
        return template.dependencies.filter(depCode => {
            const depItem = getItemState(depCode);
            return !depItem || depItem.status !== 'in_scope';
        });
    };

    // Resolve template name by code
    const getTemplateName = (code) => {
        const t = templates.find(t => t.code === code);
        return t?.name || code;
    };

    if (!sourceServiceId) {
        return <p className='text-muted'>Select a source system in Project Overview to load scope templates.</p>;
    }

    if (loading) {
        return <p className='text-muted'>Loading scope templates...</p>;
    }

    if (templates.length === 0) {
        return <p className='text-muted'>No scope templates found for this source/destination pair. Templates can be added by an admin.</p>;
    }

    const coreTemplates = templates.filter(t => (initialCategories[t.code] || t.category) === 'core');
    const addonTemplates = templates.filter(t => (initialCategories[t.code] || t.category) === 'addon');
    const setupTemplates = templates.filter(t => (initialCategories[t.code] || t.category) === 'setup');

    // Count how many setup items are marked as in_scope
    const setupInScopeCount = setupTemplates.filter(t => {
        const state = getItemState(t.code);
        return state?.status === 'in_scope';
    }).length;

    const renderTemplateRow = (template) => {
        const itemState = getItemState(template.code);
        const status = itemState?.status || 'tbd';
        const category = itemState?.category || template.category;
        const notes = itemState?.notes || '';
        const missingDeps = getMissingDependencies(template);
        const statusInfo = STATUS_OPTIONS.find(s => s.value === status);
        const categoryInfo = CATEGORY_LABELS[category];
        const isOverridden = itemState?.category && itemState.category !== template.category;

        return (
            <div key={template.code} className='d-flex align-items-stretch gap-3 py-2 px-3' style={{ borderBottom: '1px solid rgba(128,128,128,0.1)' }}>
                {/* Status toggle */}
                <div style={{ flex: '0 0 24px', paddingTop: 2, cursor: 'pointer' }} onClick={() => {
                    const next = status === 'in_scope' ? 'out_of_scope' : status === 'out_of_scope' ? 'tbd' : 'in_scope';
                    updateItem(template, 'status', next);
                }}>
                    {status === 'in_scope'
                        ? <CheckSquare size={18} color='#28a745' />
                        : <Square size={18} color={status === 'out_of_scope' ? '#6c757d' : '#ffc107'} />
                    }
                </div>

                {/* Name + description */}
                <div style={{ flex: '0 0 500px' }}>
                    <div className='d-flex align-items-center gap-2'>
                        <strong style={{ fontSize: 14 }}>{template.name}</strong>
                        <Badge color={categoryInfo?.color || 'secondary'} style={{ fontSize: 10 }}>
                            {categoryInfo?.label || category}
                        </Badge>
                        {isOverridden && <Badge color='warning' style={{ fontSize: 9 }}>overridden</Badge>}
                        <Badge color={statusInfo?.color || 'secondary'} style={{ fontSize: 10 }}>
                            {statusInfo?.label || status}
                        </Badge>
                    </div>
                    {template.description && <div className='text-muted' style={{ fontSize: 12 }}>{template.description}</div>}
                    {missingDeps.length > 0 && status === 'in_scope' && (
                        <div className='d-flex align-items-center gap-1 mt-1' style={{ fontSize: 11, color: '#dc3545' }}>
                            <AlertTriangle size={12} />
                            <span>Depends on: {missingDeps.map(id => getTemplateName(id)).join(', ')}</span>
                        </div>
                    )}
                </div>

                {/* Category override */}
                <div style={{ flex: '0 0 100px' }}>
                    <select
                        className='form-select form-select-sm'
                        value={category}
                        onChange={(e) => updateItem(template, 'category', e.target.value)}
                        style={{ fontSize: 11, height: 28 }}
                    >
                        <option value='core'>Core</option>
                        <option value='addon'>Add-on</option>
                        <option value='setup'>Setup</option>
                    </select>
                </div>

                {/* Notes */}
                <div style={{ flex: '1 1 auto', display: 'flex' }}>
                    <input
                        className='form-control form-control-sm'
                        placeholder='Notes...'
                        value={notes}
                        onChange={(e) => updateItem(template, 'notes', e.target.value)}
                        style={{ fontSize: 12, height: '100%', minHeight: 28 }}
                    />
                </div>
            </div>
        );
    };

    // ── Read-only summary view ──
    if (!isEditing) {
        const inScopeCore = coreTemplates.filter(t => getItemState(t.code)?.status === 'in_scope');
        const inScopeAddon = addonTemplates.filter(t => getItemState(t.code)?.status === 'in_scope');
        const inScopeSetup = setupTemplates.filter(t => getItemState(t.code)?.status === 'in_scope');
        const tbdItems = templates.filter(t => {
            const state = getItemState(t.code);
            return !state || state.status === 'tbd';
        });

        const renderReadOnlyRow = (template, icon) => {
            const itemState = getItemState(template.code);
            const status = itemState?.status || 'tbd';
            const notes = itemState?.notes || '';
            const missingDeps = status === 'in_scope' ? getMissingDependencies(template) : [];
            return (
                <div key={template.code} className='d-flex align-items-center gap-3 py-2 px-3' style={{ borderBottom: '1px solid rgba(128,128,128,0.1)' }}>
                    {icon || <CheckSquare size={18} color='#28a745' style={{ flex: '0 0 24px' }} />}
                    <div style={{ flex: '0 0 35%' }}>
                        <strong style={{ fontSize: 14 }}>{template.name}</strong>
                        {template.description && <div className='text-muted' style={{ fontSize: 12 }}>{template.description}</div>}
                        {missingDeps.length > 0 && (
                            <div className='d-flex align-items-center gap-1 mt-1' style={{ fontSize: 11, color: '#dc3545' }}>
                                <AlertTriangle size={12} />
                                <span>Depends on: {missingDeps.map(code => getTemplateName(code)).join(', ')}</span>
                            </div>
                        )}
                    </div>
                    <div style={{ flex: '1 1 auto', fontSize: 12, color: '#adb5bd' }}>{notes || ''}</div>
                </div>
            );
        };

        const hasAnything = inScopeCore.length > 0 || inScopeAddon.length > 0 || inScopeSetup.length > 0 || tbdItems.length > 0;

        return (
            <div className='dmt-migration-scope'>
                {!hasAnything && (
                    <p className='text-muted'>No scope items configured yet. Click Edit to define the migration scope.</p>
                )}

                {inScopeCore.length > 0 && (
                    <Card className='mb-3'>
                        <CardBody className='p-0'>
                            <div className='px-3 py-2 d-flex align-items-center gap-2' style={{ borderBottom: expandedSections.core ? '2px solid rgba(128,128,128,0.15)' : 'none', backgroundColor: 'rgba(13,110,253,0.03)', cursor: 'pointer' }} onClick={() => toggleSection('core')}>
                                {expandedSections.core ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                <strong style={{ fontSize: 13 }}>Core Data Objects</strong>
                                <Badge color='primary' style={{ fontSize: 10 }}>{inScopeCore.length}</Badge>
                            </div>
                            <Collapse isOpen={expandedSections.core}>
                                {inScopeCore.map(t => renderReadOnlyRow(t))}
                            </Collapse>
                        </CardBody>
                    </Card>
                )}

                {inScopeAddon.length > 0 && (
                    <Card className='mb-3'>
                        <CardBody className='p-0'>
                            <div className='px-3 py-2 d-flex align-items-center gap-2' style={{ borderBottom: expandedSections.addon ? '2px solid rgba(128,128,128,0.15)' : 'none', backgroundColor: 'rgba(23,162,184,0.03)', cursor: 'pointer' }} onClick={() => toggleSection('addon')}>
                                {expandedSections.addon ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                <strong style={{ fontSize: 13 }}>Add-on Data Objects</strong>
                                <Badge color='info' style={{ fontSize: 10 }}>{inScopeAddon.length}</Badge>
                            </div>
                            <Collapse isOpen={expandedSections.addon}>
                                {inScopeAddon.map(t => renderReadOnlyRow(t))}
                                {scopeData.addon_other && (
                                    <div className='px-3 py-2' style={{ borderTop: '1px solid rgba(128,128,128,0.1)' }}>
                                        <div className='text-muted' style={{ fontSize: 11 }}>Other:</div>
                                        <span style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>{scopeData.addon_other}</span>
                                    </div>
                                )}
                            </Collapse>
                        </CardBody>
                    </Card>
                )}

                {inScopeSetup.length > 0 && (
                    <Card className='mb-3'>
                        <CardBody className='p-0'>
                            <div className='px-3 py-2 d-flex align-items-center gap-2' style={{ borderBottom: expandedSections.setup ? '2px solid rgba(128,128,128,0.15)' : 'none', backgroundColor: 'rgba(52,58,64,0.03)', cursor: 'pointer' }} onClick={() => toggleSection('setup')}>
                                {expandedSections.setup ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                <strong style={{ fontSize: 13 }}>Setup / Reference Data</strong>
                                <Badge color='dark' style={{ fontSize: 10 }}>{inScopeSetup.length}</Badge>
                            </div>
                            <Collapse isOpen={expandedSections.setup}>
                                {inScopeSetup.map(t => renderReadOnlyRow(t))}
                                {scopeData.setup_other && (
                                    <div className='px-3 py-2' style={{ borderTop: '1px solid rgba(128,128,128,0.1)' }}>
                                        <div className='text-muted' style={{ fontSize: 11 }}>Other:</div>
                                        <span style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>{scopeData.setup_other}</span>
                                    </div>
                                )}
                            </Collapse>
                        </CardBody>
                    </Card>
                )}

                {tbdItems.length > 0 && (
                    <Card className='mb-3'>
                        <CardBody className='p-0'>
                            <div className='px-3 py-2 d-flex align-items-center gap-2' style={{ borderBottom: expandedSections.tbd ? '2px solid rgba(128,128,128,0.15)' : 'none', backgroundColor: 'rgba(255,193,7,0.05)', cursor: 'pointer' }} onClick={() => toggleSection('tbd')}>
                                {expandedSections.tbd ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                <strong style={{ fontSize: 13 }}>Pending Decision</strong>
                                <Badge color='warning' style={{ fontSize: 10 }}>{tbdItems.length}</Badge>
                            </div>
                            <Collapse isOpen={expandedSections.tbd}>
                                {tbdItems.map(t => renderReadOnlyRow(t, <Square size={18} color='#ffc107' style={{ flex: '0 0 24px' }} />))}
                            </Collapse>
                        </CardBody>
                    </Card>
                )}
            </div>
        );
    }

    // ── Editing view ──
    return (
        <div className='dmt-migration-scope'>
            {coreTemplates.length > 0 && (
                <Card className='mb-3'>
                    <CardBody className='p-0'>
                        <div className='px-3 py-2 d-flex align-items-center gap-2' style={{ borderBottom: expandedSections.core ? '2px solid rgba(128,128,128,0.15)' : 'none', backgroundColor: 'rgba(13,110,253,0.03)', cursor: 'pointer' }} onClick={() => toggleSection('core')}>
                            {expandedSections.core ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            <strong style={{ fontSize: 13 }}>Core Data Objects</strong>
                            <span className='text-muted' style={{ fontSize: 11 }}>Included in standard migration package</span>
                        </div>
                        <Collapse isOpen={expandedSections.core}>
                            {coreTemplates.map(renderTemplateRow)}
                        </Collapse>
                    </CardBody>
                </Card>
            )}

            {addonTemplates.length > 0 && (
                <Card className='mb-3'>
                    <CardBody className='p-0'>
                        <div className='px-3 py-2 d-flex align-items-center gap-2' style={{ borderBottom: expandedSections.addon ? '2px solid rgba(128,128,128,0.15)' : 'none', backgroundColor: 'rgba(23,162,184,0.03)', cursor: 'pointer' }} onClick={() => toggleSection('addon')}>
                            {expandedSections.addon ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            <strong style={{ fontSize: 13 }}>Add-on Data Objects</strong>
                            <span className='text-muted' style={{ fontSize: 11 }}>Optional — additional scope items</span>
                        </div>
                        <Collapse isOpen={expandedSections.addon}>
                            {addonTemplates.map(renderTemplateRow)}
                            <div className='px-3 py-2' style={{ borderTop: '1px solid rgba(128,128,128,0.1)' }}>
                                <div className='text-muted mb-1' style={{ fontSize: 11 }}>Other add-on requirements not listed above:</div>
                                <textarea
                                    className='form-control form-control-sm'
                                    rows={2}
                                    value={scopeData.addon_other || ''}
                                    onChange={(e) => {
                                        setSections(prev => ({ ...prev, migration_scope: { ...scopeData, addon_other: e.target.value } }));
                                        if (onDirtyChange) onDirtyChange(true);
                                    }}
                                    placeholder='e.g., Custom reports, document attachments, specific integrations...'
                                    style={{ fontSize: 12 }}
                                />
                            </div>
                        </Collapse>
                    </CardBody>
                </Card>
            )}

            {setupTemplates.length > 0 && (
                <Card className='mb-3'>
                    <CardBody className='p-0'>
                        <div
                            className='px-3 py-2 d-flex align-items-center justify-content-between'
                            style={{ borderBottom: expandedSections.setup ? '2px solid rgba(128,128,128,0.15)' : 'none', backgroundColor: 'rgba(52,58,64,0.03)', cursor: 'pointer' }}
                            onClick={() => toggleSection('setup')}
                        >
                            <div className='d-flex align-items-center gap-2'>
                                {expandedSections.setup ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                <strong style={{ fontSize: 13 }}>Setup / Reference Data</strong>
                                <span className='text-muted' style={{ fontSize: 11 }}>Values that must be created in destination before migration</span>
                            </div>
                            <div className='d-flex align-items-center gap-2'>
                                {setupInScopeCount > 0 && (
                                    <Badge color='success' style={{ fontSize: 10 }}>{setupInScopeCount} needs migration</Badge>
                                )}
                                <Badge color='secondary' style={{ fontSize: 10 }}>{setupTemplates.length} items</Badge>
                            </div>
                        </div>
                        <Collapse isOpen={expandedSections.setup}>
                            {setupTemplates.map(renderTemplateRow)}
                            <div className='px-3 py-2' style={{ borderTop: '1px solid rgba(128,128,128,0.1)' }}>
                                <div className='text-muted mb-1' style={{ fontSize: 11 }}>Other setup/reference data that needs to be created in destination:</div>
                                <textarea
                                    className='form-control form-control-sm'
                                    rows={2}
                                    value={scopeData.setup_other || ''}
                                    onChange={(e) => {
                                        setSections(prev => ({ ...prev, migration_scope: { ...scopeData, setup_other: e.target.value } }));
                                        if (onDirtyChange) onDirtyChange(true);
                                    }}
                                    placeholder='e.g., Custom board statuses, specific SLA configurations, tax codes...'
                                    style={{ fontSize: 12 }}
                                />
                            </div>
                        </Collapse>
                    </CardBody>
                </Card>
            )}

            <div className='text-muted' style={{ fontSize: 11 }}>
                Click the checkbox to cycle status: In Scope → Out of Scope → TBD. Category can be overridden per assessment.
            </div>
        </div>
    );
};

export default MigrationScopeSection;
